import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Target, 
  Eye, 
  Heart, 
  Award,
  Users,
  Shield,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import Newsletter from '../components/Newsletter';

const values = [
  {
    icon: Heart,
    title: 'Patient-Centric',
    description: 'Every solution we develop is designed with patient outcomes in mind.',
  },
  {
    icon: Shield,
    title: 'Security First',
    description: 'We prioritize data security and HIPAA compliance in all our solutions.',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We work closely with healthcare providers to understand their unique needs.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We strive for excellence in every project we undertake.',
  },
];

const milestones = [
  { year: '2009', event: 'Founded in Silicon Valley' },
  { year: '2012', event: 'First major hospital partnership' },
  { year: '2015', event: 'Expanded to 50+ clients' },
  { year: '2018', event: 'Launched AI-powered solutions' },
  { year: '2021', event: 'Reached 500+ healthcare partners' },
  { year: '2024', event: 'Global expansion initiative' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function About() {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
                About Us
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
                Pioneering Healthcare Technology Since 2009
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
                CureClave Innovations was founded with a simple mission: to make healthcare technology accessible, efficient, and patient-focused. Today, we're proud to partner with over 500 healthcare organizations worldwide.
              </p>
              <div className="flex flex-wrap gap-8">
                <div>
                  <p className="font-heading text-4xl font-bold text-cyan-600">15+</p>
                  <p className="text-slate-600">Years Experience</p>
                </div>
                <div>
                  <p className="font-heading text-4xl font-bold text-cyan-600">500+</p>
                  <p className="text-slate-600">Partners</p>
                </div>
                <div>
                  <p className="font-heading text-4xl font-bold text-cyan-600">98%</p>
                  <p className="text-slate-600">Satisfaction</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&q=80&w=800"
                  alt="CureClave Team"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-8 md:p-12 text-white"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                <Target className="w-7 h-7" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-4">Our Mission</h2>
              <p className="text-cyan-100 text-lg leading-relaxed">
                To empower healthcare organizations with innovative technology solutions that improve patient outcomes, streamline operations, and ensure regulatory compliance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-slate-900 rounded-3xl p-8 md:p-12 text-white"
            >
              <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-cyan-400" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-4">Our Vision</h2>
              <p className="text-slate-300 text-lg leading-relaxed">
                To be the global leader in healthcare technology innovation, setting the standard for excellence in digital health transformation and patient care improvement.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
              What Drives Us
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Our Core Values
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-cyan-50 flex items-center justify-center text-cyan-600 mx-auto mb-6">
                  <value.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
              Our Journey
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Key Milestones
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-slate-200 hidden md:block"></div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-8"
            >
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className={`flex items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className={`inline-block bg-white rounded-2xl p-6 shadow-sm border border-slate-100 ${
                      index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'
                    }`}>
                      <p className="font-heading text-2xl font-bold text-cyan-600 mb-2">{milestone.year}</p>
                      <p className="text-slate-600">{milestone.event}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-cyan-500 border-4 border-white shadow hidden md:block"></div>
                  <div className="flex-1 hidden md:block"></div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Want to Join Our Journey?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Whether you're looking to partner with us or join our team, we'd love to hear from you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  className="btn-gradient text-white rounded-full px-8 py-4 text-base font-semibold shadow-lg shadow-cyan-500/20"
                  data-testid="about-contact-cta"
                >
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/team">
                <Button 
                  variant="outline"
                  className="rounded-full px-8 py-4 text-base font-medium border-slate-200 hover:bg-white"
                  data-testid="about-team-cta"
                >
                  Meet Our Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
